import React, { createContext, useState, useContext, useEffect } from 'react';
import AppContext from 'AppContext';
import { createAPIClient } from 'api/client';
import { useUserState } from 'hooks/useUserState';

export const TeamContext = createContext();

export const TeamProvider = ({ children }) => {
  const { platformUser } = useContext(AppContext);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get teams from UserContext
  const userState = useUserState();

  useEffect(() => {
    const fetchTeam = async () => {
      // If UserContext is still loading, wait
      if (userState.isLoading) {
        return;
      }
      
      if (!platformUser) {
        setCurrentTeamId(null);
        setLoading(false);
        return;
      }

      // Try to use teams from UserContext first
      if (userState.teams) {
        const adminTeams = userState.teams.admin || [];
        const memberTeams = userState.teams.member || [];
        
        if (adminTeams.length > 0) {
          // console.log('Using admin team from UserContext:', adminTeams[0].teamId);
          setCurrentTeamId(adminTeams[0].teamId);
          setLoading(false);
          return;
        } else if (memberTeams.length > 0) {
          // console.log('Using member team from UserContext:', memberTeams[0].teamId);
          setCurrentTeamId(memberTeams[0].teamId);
          setLoading(false);
          return;
        }
      }

      // Fall back to API call if no teams in UserContext
      try {
        console.log('Falling back to API call for team data');
        const api = await createAPIClient();
        const data = await api.teams.getCurrentTeam();

        if (!data?.team_id) {
          console.warn('No active team found for user');
          setCurrentTeamId(null);
        } else {
          console.log('Found team from API:', data.team_id);
          setCurrentTeamId(data.team_id);
        }
      } catch (error) {
        console.error('Error in team fetch:', error);
        setCurrentTeamId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [platformUser, userState.teams, userState.isLoading]);

  return (
    <TeamContext.Provider value={{ currentTeamId, setCurrentTeamId, loading }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}; 